// DashboardRouter
// ---------------
// Single switch that maps the currently selected sidebar key to its page
// component. Pulled out of MainDashboard so adding a new route is a one-line
// change here, not a 2,000-line scroll.
//
// Sprint 5 / 5.3-5.5: pages with heavy bundles (recharts, mapbox-gl, jsPDF)
// are loaded via React.lazy so the initial dashboard payload doesn't pay
// for them. The Suspense boundary shows a small spinner while a chunk is
// fetched. Eagerly-imported pages are the small/common ones (Owners,
// Condos, etc.); the chunky ones are deferred.
import React, { Suspense } from 'react';
import { Spin } from 'antd';

import { Condos }            from '../../pages/Condos';
import { Owners }            from '../../pages/Owners';
import FeeManagement         from '../../pages/FeeManagement';
import FinancialManagement   from '../../pages/FinancialManagement';
import { Statements }        from '../../pages/Statements';
import { Documents }         from '../../pages/Documents';
import { OwnerPortal }       from '../../pages/owner/Portal';
import { Payments }          from '../../pages/Payments';
import { Violations }        from '../../pages/Violations';
import MaintenanceHub        from '../../pages/MaintenanceHub';
import Amenities             from '../../pages/Amenities';
import { BoardManagement }   from '../../pages/BoardManagement';
import BoardMemberPortal     from '../../pages/BoardMemberPortal';
import AdminOperationsHub    from '../../pages/AdminOperationsHub';
import CommunicationsHub     from '../../pages/CommunicationsHub';
import { FeaturePermissions } from '../../pages/FeaturePermissions';
// Sprint 39 — per-condo capability gates (distinct from
// FeaturePermissions, which is per-role sidebar visibility).
import FeatureToggles from '../../pages/FeatureToggles';
import { Tutorial }          from '../../pages/Tutorial';
import MyHomeHub             from '../../pages/MyHomeHub';
import Units                 from '../../pages/Units';
// Sprint 83 (ADR-082) — Atrium Owner home, gated behind the atriumRedesign flag.
// When the flag is OFF the owner sees the unchanged OwnerPortal; when ON they
// get the new Salesforce-style home fed the same real scoped data.
import { useAtriumRedesign } from '../../hooks/useAtriumRedesign';
import { AtriumOwnerHome }   from '../atrium/home/AtriumOwnerHome';
// Sprint 84 (ADR-082) — Manager / Board / Super-Admin Atrium homes. Gated behind
// the same atriumRedesign flag; flag OFF renders the existing components.
import { AtriumManagerHome }    from '../atrium/home/AtriumManagerHome';
import { AtriumBoardHome }      from '../atrium/home/AtriumBoardHome';
import { AtriumSuperAdminHome } from '../atrium/home/AtriumSuperAdminHome';

import type { DashboardComponent } from './menuConfig';
import type { OverviewProps } from './OverviewComponent';
import { OverviewComponent } from './OverviewComponent';

// ── Lazy-loaded heavy pages ───────────────────────────────────────────────
// Recharts (Reports), mapbox-gl (Property Operations / Parking), jsPDF
// (Status Certificates) each pull ~200-500kB into the bundle. Defer their
// fetch until the user actually navigates to one of these routes.
const ReportsAndAnalytics    = React.lazy(() => import('../../pages/ReportsAndAnalytics'));
const StatusCertificates     = React.lazy(() =>
  import('../../pages/StatusCertificates').then(m => ({ default: m.StatusCertificates }))
);
const PropertyOperationsHub  = React.lazy(() => import('../../pages/PropertyOperationsHub'));
// Sprint 90 — F42 AGM packet builder. Server-side PDF; capability gated on
// agm_packet_bulk inside the page. Lazy like the other governance pages.
const AgmPackets             = React.lazy(() => import('../../pages/AgmPackets'));

export interface DashboardRouterProps {
  selected: DashboardComponent;
  userRole: string | undefined;
  // Props the overview page needs (the only page that pulls live state from
  // the dashboard). Everything else is self-contained.
  overview: OverviewProps;
}

/** Page-level fallback while a lazy chunk is in flight. Small, neutral. */
function ChunkLoading() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
      <Spin size="large" />
    </div>
  );
}

export const DashboardRouter: React.FC<DashboardRouterProps> = ({
  selected,
  userRole,
  overview,
}) => {
  // Sprint 83 (ADR-082) — when the flag is on AND the user is an owner, the
  // owner home routes render the Atrium home instead of OwnerPortal. Reversible:
  // flag off ⇒ OwnerPortal unchanged for every owner route.
  const atrium = useAtriumRedesign();
  const isOwner = userRole === 'owner';
  const ownerHome = atrium && isOwner ? <AtriumOwnerHome /> : <OwnerPortal />;

  // Sprint 84 (ADR-082) — when the flag is on, the role's home (the `overview`
  // case AND the role default) renders the matching Atrium home. Determined from
  // auth role (authStore User.role): owner | admin | super_admin_condo |
  // board-member (legacy super-admin aliases tolerated). Flag OFF ⇒ the existing
  // OverviewComponent renders unchanged for every role.
  const isSuperAdmin =
    userRole === 'super_admin_condo' || userRole === 'superadmin' || userRole === 'super_admin';
  const isBoard = userRole === 'board-member' || userRole === 'board';
  const isManager = userRole === 'admin';

  /** The Atrium home for the current role, or null if no Atrium home applies. */
  const atriumRoleHome: React.ReactNode | null = !atrium
    ? null
    : isOwner
      ? <AtriumOwnerHome />
      : isSuperAdmin
        ? <AtriumSuperAdminHome />
        : isBoard
          ? <AtriumBoardHome />
          : isManager
            ? <AtriumManagerHome />
            : null;

  // Wrap every render in a single Suspense — only the lazy pages need it,
  // but the boundary is a no-op for the eager ones.
  return (
    <Suspense fallback={<ChunkLoading />}>
      {(() => {
        switch (selected) {
          case 'overview':             return atriumRoleHome ?? <OverviewComponent {...overview} />;
          case 'condos':               return <Condos />;
          case 'owners':               return <Owners />;
          case 'fee-management':       return <FeeManagement />;
          case 'financial-management': return <FinancialManagement />;
          case 'reports-analytics':    return <ReportsAndAnalytics />;
          case 'statements':           return <Statements />;
          case 'documents':            return <Documents />;
          case 'portal':               return ownerHome;
          case 'payments':             return <Payments />;
          case 'status-certificates':  return <StatusCertificates />;
          case 'agm-packets':          return <AgmPackets />;
          case 'violations':           return <Violations />;
          case 'maintenance-hub':      return <MaintenanceHub />;
          case 'amenities':            return <Amenities />;
          case 'board':                return <BoardManagement />;
          case 'board-member-portal':  return <BoardMemberPortal />;
          case 'admin-operations':     return <AdminOperationsHub />;
          case 'property-operations':  return <PropertyOperationsHub />;
          case 'communications':       return <CommunicationsHub />;
          case 'feature-permissions':  return <FeaturePermissions />;
          case 'feature-toggles':      return <FeatureToggles />;
          case 'tutorial':             return <Tutorial />;
          case 'my-home':              return atrium && isOwner ? <AtriumOwnerHome /> : <MyHomeHub />;
          case 'units':                return <Units />;
          default:
            // Sprint R11-8 (ADR-128) — every key in `DashboardComponent` has a
            // case above, and useMenuState resolves unknown URL slugs to a 404
            // before we get here, so this is now unreachable in practice. It
            // stays as a type-exhaustiveness backstop: if a key is ever added
            // to the registry without a case, render the role home rather than
            // a blank pane (a wrong-but-usable page beats a white screen).
            return isOwner
              ? ownerHome
              : (atriumRoleHome ?? <OverviewComponent {...overview} />);
        }
      })()}
    </Suspense>
  );
};
